import { useState } from 'react';
import type { AIModel, Message } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { useSession } from '../state/session.js';
import { useUI } from '../state/ui.js';
import { useVoiceConversation } from '../hooks/useVoiceConversation.js';
import type { SendInput } from '../hooks/useChat.js';
import type { TranslationKey } from '../i18n/index.js';
import { Icon } from './Icon.js';
import { IconButton } from './IconButton.js';
import { Modal } from './Modal.js';
import { VoiceSettingsPanel } from './VoiceSettingsPanel.js';

export interface VoiceModeOverlayProps {
  open: boolean;
  onClose: () => void;
  /** The model chosen in the composer (the call may switch to a quicker one). */
  modelId: string | undefined;
  models: AIModel[];
  messages: Message[];
  streaming: boolean;
  send: (input: SendInput) => void;
  stop: () => void;
}

const STATUS_KEYS = {
  starting: 'voice.status.starting',
  listening: 'voice.status.listening',
  transcribing: 'voice.status.transcribing',
  thinking: 'voice.status.thinking',
  speaking: 'voice.status.speaking',
  error: 'voice.status.error',
} as const satisfies Record<string, TranslationKey>;

const PROBLEM_KEYS: Record<string, TranslationKey> = {
  MIC_DENIED: 'voice.micDenied',
  MIC_UNAVAILABLE: 'voice.micUnavailable',
  VOICE_TTS_FALLBACK: 'voice.ttsFallback',
};

/** Full-screen hands-free conversation: speak, the assistant answers out loud, repeat. */
export function VoiceModeOverlay({ open, onClose, modelId, models, messages, streaming, send, stop }: VoiceModeOverlayProps) {
  const { t, locale } = useI18n();
  const { api } = useSession();
  const { toast } = useUI();
  const [showSettings, setShowSettings] = useState(false);
  const [held, setHeld] = useState(false);

  const voice = useVoiceConversation({
    open,
    api,
    language: locale,
    chatModelId: modelId,
    models,
    messages,
    streaming,
    send,
    stop,
    onProblem: (code, fatal) => {
      const key = PROBLEM_KEYS[code] ?? (`error.${code}` as TranslationKey);
      toast(t(key), fatal ? 'error' : 'info');
    },
    onIdleTimeout: onClose,
  });

  const push = voice.settings.talkMode === 'push';
  const alive = voice.status !== 'starting' && voice.status !== 'error';
  const canInterrupt = voice.status === 'thinking' || voice.status === 'speaking';
  // Hands-free: the circle only interrupts. Push-to-talk: it is the talk button (and interrupts too).
  const orbEnabled = push ? alive && voice.status !== 'transcribing' : canInterrupt;
  const statusKey = voice.fatalCode ? STATUS_KEYS.error : STATUS_KEYS[voice.status];
  const fatalKey = voice.fatalCode
    ? (PROBLEM_KEYS[voice.fatalCode] ?? (`error.${voice.fatalCode}` as TranslationKey))
    : null;
  const orbLabel = push ? t('voice.holdToTalk') : canInterrupt ? t('voice.interrupt') : t(statusKey);

  function release() {
    setHeld(false);
    voice.releaseToTalk();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('voice.title')}
      closeLabel={t('common.close')}
      fullscreen
      className="voice-overlay"
      headerEnd={
        <IconButton
          icon="settings"
          label={t('voice.settings')}
          size={18}
          active={showSettings}
          onClick={() => setShowSettings((value) => !value)}
        />
      }
    >
      <div className="voice-body">
        {showSettings ? (
          <VoiceSettingsPanel settings={voice.settings} onChange={voice.updateSettings} voiceModel={voice.voiceModel} />
        ) : (
          <>
            <button
              type="button"
              className={`voice-orb voice-orb--${voice.status}${orbEnabled ? ' is-tappable' : ''}${held ? ' is-held' : ''}`}
              style={{ ['--voice-level' as string]: voice.level }}
              disabled={!orbEnabled}
              aria-label={orbLabel}
              aria-pressed={push ? held : undefined}
              onClick={push ? undefined : voice.interrupt}
              onPointerDown={
                push
                  ? (event) => {
                      event.currentTarget.setPointerCapture?.(event.pointerId);
                      setHeld(true);
                      voice.pressToTalk();
                    }
                  : undefined
              }
              onPointerUp={push ? release : undefined}
              onPointerCancel={push ? release : undefined}
              onContextMenu={(event) => event.preventDefault()}
            >
              <Icon name={voice.status === 'listening' ? 'mic' : 'waveform'} size={44} />
            </button>

            <p className="voice-status" role="status" aria-live="polite">
              {fatalKey ? t(fatalKey) : push && voice.status === 'listening' ? t(held ? 'voice.releaseToSend' : 'voice.holdToTalk') : t(statusKey)}
            </p>
            {canInterrupt && !push && <p className="voice-hint">{t('voice.tapToInterrupt')}</p>}
            {voice.status === 'listening' && !voice.userText && !push && <p className="voice-hint">{t('voice.hint')}</p>}

            <div className="voice-captions" aria-live="off">
              {voice.userText && <p className="voice-caption voice-caption--user">{voice.userText}</p>}
              {voice.assistantText && (
                <p className="voice-caption voice-caption--assistant">{tail(voice.assistantText, 280)}</p>
              )}
            </div>
          </>
        )}

        <button type="button" className="btn btn-danger voice-end" onClick={onClose}>
          {t('voice.end')}
        </button>
      </div>
    </Modal>
  );
}

/** Captions follow the speech: show the latest part of a long answer. */
function tail(text: string, max: number): string {
  return text.length <= max ? text : `…${text.slice(text.length - max).replace(/^\S*\s/, '')}`;
}
